<template>
  <!-- Register Container -->
  <div
    class="px-2 py-8 mt-5 bg-center bg-cover flex flex-col items-center justify-center w-full"
  >
    <div class="max-w-3/4">
      <!-- Content STEP 1-->
      <RegisterBox v-if="subscriptionTemplates && !subscriptionTemplate">
        <template v-slot:header>
          <p>
            <small class="text-lg md:text-xl lg:text-2xl">Step 1 - </small>
            <span class="text-lg lg:text-4xl">Select template</span>
          </p>
        </template>
        <template>
          <div class="flex flex-wrap justify-center">
            <div v-for="(s, i) in subscriptionTemplates" v-bind:key="s.name">
              <RegisterSelectBox
                @input="selectSubscriptionTemplate"
                :value="{ ...s, index: i }"
              />
            </div>
          </div>
        </template>
      </RegisterBox>
      <!-- Content STEP 2-->
      <RegisterBox v-if="subscriptionTemplates && subscriptionTemplate">
        <template v-slot:header>
          <p>
            <small class="text-lg md:text-xl lg:text-2xl">Step 2 - </small>
            <span class="text-lg lg:text-4xl">Finalize your registration</span>
          </p>
          <p class="text-xl lg:text-2xl">
            You have selected:
            <span class="text-2xl lg:text-4xl text-highlight">{{
              subscriptionTemplate.name
            }}</span>
          </p>
          <p class="text-xl lg:text-2xl">
            Subscription level:
            <span class="text-2xl lg:text-4xl text-highlight">{{
              subscriptionTemplate.subscriptionType
            }}</span>
          </p>

          <Button
            class="object-none object-right-bottom w-64 text-xs md:w-96 md:text-base"
            @click="goBack"
            >Go back - Select other subscription</Button
          >
        </template>
        <div class="flex flex-col">
          <div
            class="flex flex-col items-center justify-center mt-6 w-full space-y-2 lg:max-w-1/2 mx-auto"
          >
            <MicrosoftButton @click="() => getUserInfo('microsoft')" />
            <GoogleButton @click="() => getUserInfo('google')" />
          </div>
          <div class="flex mx-auto my-2">
            <HorizontalLineText>
              <span class="text-xs">OR</span>
            </HorizontalLineText>
          </div>
          <v-input
            :labelPlaceholder="'Email'"
            :disabled="!!this.oidc"
            :type="'email'"
            v-model="email"
          />
          <v-input
            :labelPlaceholder="'Verify email'"
            :disabled="!!this.oidc"
            :type="'email'"
            v-model="verifyEmail"
          />
          <v-input :labelPlaceholder="'Full Name'" v-model="fullName" />
          <v-input :labelPlaceholder="'Phone'" v-model="phone" />
          <v-input :labelPlaceholder="'Company name'" v-model="companyName" />
          <v-input
            :labelPlaceholder="'VAT / Organization number'"
            v-model="orgNumber"
          />
          <v-input :labelPlaceholder="'Address'" v-model="address" />
          <v-input :labelPlaceholder="'Zip code'" v-model="zipCode" />
          <v-input :labelPlaceholder="'City'" v-model="city" />
          <v-input :labelPlaceholder="'Country'" v-model="country" />
          <div :style="{ textAlign: 'left' }">
            *All fields are required
          </div>
          <div class="mt-8">
            <Checkbox :value="policyAccepted" @input="policyChecked" />
            <span class="mb-3 ml-3"
              >I have read and I understand the
              <a href="/#/policy" target="_blank" class="text-white"
                >Privacy policy</a
              ></span
            >
          </div>
          <div :style="{ textAlign: 'right' }">
            <Button
              class="object-none object-right-bottom w-64 mt-5 mb-10"
              :class="{ disable: email === '' || !policyAccepted }"
              :disabled="!policyAccepted"
              @click="doRegister"
              >Register</Button
            >
          </div>
        </div>
      </RegisterBox>
      <Loading
        v-if="registrationStatus === 'inprogress'"
        :heading="'Registration in progress...'"
        :description="'Please wait while we create your account...'"
      />
    </div>

    <modal
      v-if="registrationErrors"
      type="error"
      @close="registrationErrors = null"
      heading="Registration failed"
    >
      <template slot="body">
        <div>
          <h3>Registration failed</h3>
          <hr />
          <p v-for="(err, i) in registrationErrors" v-bind:key="i">
            {{ err.message }}
          </p>
        </div>
      </template>
      <template v-slot:buttons>
        <div>
          <Button
            class="ml-2 big bg-primary text-textcolor"
            @click="registrationErrors = null"
            >OK</Button
          >
        </div>
      </template>
    </modal>

    <modal
      v-if="registrationStatus == 'completed'"
      type="success"
      heading="Registration completed"
      @close="$router.push({ name: 'login' })"
    >
      <template slot="body">
        <div>
          <h3>Registration completed</h3>
          <hr />
          <p>
            We have sent you an email with instructions on how to login.
          </p>
        </div>
      </template>
      <template v-slot:buttons>
        <div>
          <Button
            class="ml-2 big bg-primary text-textcolor"
            @click="$router.push({ name: 'login' })"
          >
            Go to login
          </Button>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import vInput from "components/forms/Input.vue";
import Button from "components/forms/Button.vue";
import Modal from "../components/Modal";
import Loading from "../components/Loading";
import RegisterBox from "../components/RegisterBox";
import RegisterSelectBox from "../components/RegisterSelectBox.vue";
import Checkbox from "../components/forms/Checkbox";
import MicrosoftButton from "../components/MicrosoftButton";
import GoogleButton from "../components/GoogleButton";
import HorizontalLineText from "../components/HorizontalLineText";
import { hashStringToObject } from "../utils/stringHelpers";
import { redirectUriProvider } from "../store/modules/auth";

export default {
  name: "Login",
  data() {
    return {
      subscriptionTemplate: null,
      email: null,
      verifyEmail: null,
      fullName: null,
      phone: null,
      companyName: null,
      orgNumber: null,
      address: null,
      city: null,
      zipCode: null,
      country: null,
      registrationStatus: null,
      registrationErrors: null,
      policy: null,
      policyAccepted: false,
    };
  },
  components: {
    vInput,
    Button,
    Modal,
    Loading,
    RegisterBox,
    RegisterSelectBox,
    Checkbox,
    MicrosoftButton,
    GoogleButton,
    HorizontalLineText,
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    await this.getLatestPolicy();
    this.loading = false;
    this.$gtm.enable(true);
    const recaptcha = this.$recaptchaInstance;
    recaptcha.showBadge();

    if (this.oidc) {
      await this.getProviderInfo();
    }

    this.subscriptionTemplate = this.$route.query.subscriptionTemplate
      ? this.subscriptionTemplates[this.$route.query.subscriptionTemplate - 1]
      : null;

    this.$gtm.trackView("Register", "/register");
  },
  beforeDestroy() {
    this.$gtm.enable(false);
    const recaptcha = this.$recaptchaInstance;
    recaptcha.hideBadge();
  },
  methods: {
    selectSubscriptionTemplate(value) {
      this.subscriptionTemplate = value;
      this.$router.replace({
        query: { subscriptionTemplate: value.index + 1 },
      });
    },
    async getUserInfo(method) {
      try {
        await this.$store.dispatch("AUTH/PROVIDER_LOGIN", {
          method,
          type: "REGISTER",
          state: this.$route.query.subscriptionTemplate,
        });
      } catch (error) {
        this.loading = false;
        this.modalType = "error";
        this.modalHeader = "Something went wrong";
        this.showModal = true;
      }
    },
    async getLatestPolicy() {
      const response = await this.$store.$api.get("/api/account/invite/policy");
      this.policy = response.data.data;
    },
    policyChecked() {
      this.policyAccepted = !this.policyAccepted;
    },
    async getData() {
      await this.$store.dispatch("SUBSCRIPTIONS/GET_SUBSCRIPTION_TEMPLATES");
    },
    async doRegister() {
      await this.$recaptchaLoaded();
      const token = await this.$recaptcha("register");
      const registerModel = {
        subscriptionName: this.subscriptionTemplate.name,
        subscriptionType: this.subscriptionTemplate.subscriptionType,
        email: this.email,
        verifyEmail: this.verifyEmail,
        fullName: this.fullName,
        phone: this.phone,
        companyName: this.companyName,
        orgNumber: this.orgNumber,
        address: this.address,
        city: this.city,
        zipCode: this.zipCode,
        country: this.country,
        token: token,
      };

      this.registrationStatus = "inprogress";
      this.registrationErrors = null;
      var response = await this.$store.dispatch(
        "SUBSCRIPTIONS/REGISTER",
        registerModel
      );
      if (
        response.error &&
        response.error.errors &&
        response.error.errors.length > 0
      ) {
        this.registrationStatus = null;
        this.registrationErrors = response.error.errors;
      } else {
        this.registrationStatus = "completed";

        this.$gtm.trackEvent({
          event: "account-registered",
          category: "Register",
          action: "click",
          label: "Register successful",
          value: registerModel.subscriptionName,
        });
      }
    },
    goBack() {
      this.$gtm.trackEvent({
        event: "click-go-back",
        category: "Register",
        action: "click",
        label: `Clicked go back on Step 2`,
        value: `Previous template ${this.subscriptionTemplate.name}`,
      });

      this.$router.replace({ query: undefined });
      this.subscriptionTemplate = null;
    },
    showErrorMessage(error) {
      if (!error) return;
      if (error.status === 422) return error.data;
      return error.data.errors.Password[0];
    },
    async getProviderInfo() {
      try {
        const response = await this.$store.$auth.post(
          "/api/account/userInfoOidc",
          {
            code: this.oidc.code,
            state: decodeURIComponent(this.oidc.state),
            redirect_uri: redirectUriProvider,
          }
        );
        this.email = response.data.email;
        this.verifyEmail = response.data.email;
        this.fullName = [response.data.firstName, response.data.lastName]
          .filter(Boolean)
          .join(" ");
      } catch (error) {
        this.$router.push({
          name: "register",
          query: { ...this.$route.query, oidc: undefined },
        });
      }
    },
  },
  watch: {
    "$route.query": {
      async handler(query) {
        this.oidc = query.oidc ? hashStringToObject(query.oidc) : null;
      },
      immediate: true,
    },
  },
  computed: {
    subscriptionTemplates() {
      return this.$store.getters["SUBSCRIPTIONS/SUBSCRIPTION_TEMPLATES"];
    },
    errorMessage() {
      return this.$store.getters["AUTH/ERROR_MESSAGE"];
    },
    redirectPath() {
      return this.$store.getters["AUTH/REDIRECT_PATH"];
    },
  },
};
</script>

<style lang="postcss" scoped>
.button {
  background-color: theme("colors.primary-light");
  height: 60px;
  color: white;
  border: solid 1px theme("colors.secondary");
}
.selectsection {
  max-width: 90%;
  padding: 5px;
  margin: 10px;
  background-color: theme("colors.primary");
  border: 1px solid theme("colors.primary-light");
  border-radius: 5px;
  box-shadow: 0px 0px 10px theme("colors.primary-dark");
}

.register {
  min-height: calc(100vh - theme("variables.header-size") * 3);
  @apply;
}

.registerbox {
  transform: translate(-50%, 0%);
  left: 50%;
  box-shadow: 0px 0px 10px theme("colors.primary");
  position: absolute;
  width: 800px;
  border-radius: 5px;

  /* @apply absolute w-px500 bg-primary rounded; */
}

.registerbox > * {
  @apply my-12;
}

input {
  @apply mb-6;
}

.forgot {
  @apply mt-6;
}

.top-text {
  @apply h-32 relative text-center text-3xl;
}

.input-wrapper {
  @apply relative;
}
</style>
